import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { FinanceApplicationModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/financeApplication/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/financeApplication/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/financeApplication/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/financeApplication/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/financeApplication/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/financeApplication/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/financeApplication/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/financeApplication/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/financeApplication/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/financeApplication/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/financeApplication/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/financeApplication/DELETE_FAILURE'

export type FinanceApplicationModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: FinanceApplicationModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadFinanceApplications(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().financeApplications.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/finance_applications.json', options)
      .then(({ data }: { data: FinanceApplicationModel[] }) => {
        const normalizedJson = normalize(data, Schemas.FINANCE_APPLICATION_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadFinanceApplication(financeApplicationId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.financeApplications.loadedForKeys]
    const loadedIds = [...state.financeApplications.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (financeApplicationId && !loadedIds.includes(financeApplicationId)){
      loadedIds.push(financeApplicationId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/finance_applications/${financeApplicationId}.json`, options)
      .then(({ data }: { data: FinanceApplicationModel }) => {
        const normalizedJson = normalize(data, Schemas.FINANCE_APPLICATION)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createFinanceApplication(
  financeApplication: Partial<FinanceApplicationModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      finance_application: financeApplication,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/finance_applications.json', options, config)
      .then(({ data }: { data: FinanceApplicationModel }) => {
        const normalizedJson = normalize(data, Schemas.FINANCE_APPLICATION)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateFinanceApplication(
  financeApplication: UpdateParams<FinanceApplicationModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      finance_application: financeApplication,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/finance_applications/${financeApplication.id}.json`, options, config)
      .then(({ data }: { data: FinanceApplicationModel }) => {
        const normalizedJson = normalize(data, Schemas.FINANCE_APPLICATION)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createOrUpdateFinanceApplicationKey(
  params: {
    incomingFinanceApplication: FinanceApplicationModel,
    key: string,
    value: string | number | object,
  },
) {
  const { incomingFinanceApplication, key, value } = params

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const { financeApplications } = state.entities

    const financeApplication = financeApplications[incomingFinanceApplication.id]

    // If already in Redux
    // Total always should be incoming
    if (financeApplication){
      const updatedFinanceApp = { ...financeApplication, [key]: value, total: incomingFinanceApplication.total }

      const normalizedJson = normalize(updatedFinanceApp, Schemas.FINANCE_APPLICATION)
      dispatch(updateEntities(normalizedJson))
      dispatch(updateSuccess())
    } else if (!financeApplication){
      const normalizedJson = normalize(incomingFinanceApplication, Schemas.FINANCE_APPLICATION)
      dispatch(updateEntities(normalizedJson))
      dispatch(createSuccess())
    } else {
      dispatch(updateFailure(['Error creating or updating Finance Application.']))
    }
  }
}

export function generateFinanceApplicationUrl(
  financeApplication: UpdateParams<FinanceApplicationModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/finance_applications/${financeApplication.id}/generate_external_url.json`, options, config)
      .then(({ data }: { data: FinanceApplicationModel }) => {
        const normalizedJson = normalize(data, Schemas.FINANCE_APPLICATION)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteFinanceApplication(financeApplication: DeleteParams<FinanceApplicationModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/finance_applications/${financeApplication.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.FINANCE_APPLICATION)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: FinanceApplicationModuleState = initialState,
  action: Action = { type: '' },
): FinanceApplicationModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
